import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { findCourseBySlug } from '@/lib/content';
import { fluencyAttemptStore } from '@/lib/storage';
import { shuffle } from '@/lib/quiz';
import type { FluencyQuestion } from '@/types';

const QUESTION_COUNT = 10;
const DURATION_MS = 60_000;
const LETTERS = 'ABCDEFGH';

type Phase = 'idle' | 'playing' | 'done';

export default function Fluency() {
  const { courseSlug = '' } = useParams();
  const course = findCourseBySlug(courseSlug);

  const [phase, setPhase] = useState<Phase>('idle');
  const [questions, setQuestions] = useState<FluencyQuestion[]>([]);
  const [answers, setAnswers] = useState<Array<number | null>>([]);
  const [cur, setCur] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const finishedRef = useRef(false);

  useEffect(() => {
    if (phase !== 'playing') return;
    const id = setInterval(() => setNow(Date.now()), 50);
    const onVis = () => { if (!document.hidden) setNow(Date.now()); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing' || finishedRef.current) return;
    if (now - startedAt >= DURATION_MS) finish();
  }, [now, phase, startedAt]);

  if (!course) return <Navigate to="/" replace />;

  const fluency = course.fluency;
  const best = fluencyAttemptStore.bestScore(course.slug);

  if (!fluency || fluency.questions.length === 0) {
    return (
      <div className="start-screen">
        <div className="badge">{course.code} · Fluency</div>
        <h1>Not ready yet</h1>
        <div className="empty-unit-card">
          <h3>No fluency questions</h3>
          <p>Add questions to <code>src/content/{course.slug}/fluency.json</code> and they'll appear here.</p>
        </div>
        <Link to="/" className="back-link">← All courses</Link>
      </div>
    );
  }

  const remainingMs = phase === 'playing' ? Math.max(0, DURATION_MS - (now - startedAt)) : DURATION_MS;
  const remainingSec = Math.ceil(remainingMs / 1000);
  const lowTime = remainingMs <= 10_000;

  const start = () => {
    finishedRef.current = false;
    const pool = shuffle(fluency.questions).slice(0, Math.min(QUESTION_COUNT, fluency.questions.length));
    setQuestions(pool);
    setAnswers(new Array(pool.length).fill(null));
    setCur(0);
    setStartedAt(Date.now());
    setNow(Date.now());
    setPhase('playing');
  };

  const select = (i: number) => {
    if (finishedRef.current || phase !== 'playing') return;
    if (Date.now() - startedAt >= DURATION_MS) {
      finish();
      return;
    }
    setAnswers((prev) => {
      const next = [...prev];
      next[cur] = i;
      return next;
    });
  };

  const next = () => {
    if (finishedRef.current || phase !== 'playing') return;
    if (Date.now() - startedAt >= DURATION_MS) {
      finish();
      return;
    }
    if (cur >= questions.length - 1) {
      finish();
      return;
    }
    setCur((c) => c + 1);
  };

  function finish() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    const completedAt = Date.now();
    const score = questions.reduce((acc, q, i) => acc + (answers[i] === q.ans[0] ? 1 : 0), 0);
    fluencyAttemptStore.append({
      courseSlug: course!.slug,
      startedAt,
      completedAt,
      durationMs: completedAt - startedAt,
      score,
      total: questions.length,
      questions,
      answers,
    });
    setPhase('done');
  }

  if (phase === 'idle') {
    return (
      <div className="start-screen">
        <div className="badge">{course.code} · Fluency</div>
        <h1>Fluency <span>Quiz</span></h1>
        <p className="subtitle">10 random questions · 60 seconds · single answer</p>
        <div className="ver">{fluency.questions.length} in pool</div>
        <div className="stats">
          <div className="stat">
            <div className="stat-num">10</div>
            <div className="stat-label">Questions</div>
          </div>
          <div className="stat">
            <div className="stat-num">60s</div>
            <div className="stat-label">Time</div>
          </div>
          {best && (
            <div className="stat">
              <div className="stat-num">{best.pct}%</div>
              <div className="stat-label">Best</div>
            </div>
          )}
        </div>
        <div className="start-buttons">
          <button className="btn btn-primary" onClick={start}>Start →</button>
        </div>
        <Link to="/" className="back-link">← All courses</Link>
      </div>
    );
  }

  if (phase === 'playing') {
    const q = questions[cur];
    const selected = answers[cur];
    return (
      <>
        <div className="progress-bar">
          <div className="progress-header">
            <span className="progress-label">{cur + 1} / {questions.length}</span>
            <span className={`fluency-timer ${lowTime ? 'low' : ''}`}>
              0:{String(remainingSec).padStart(2, '0')}
            </span>
          </div>
          <div className="progress-track">
            <div
              className={`progress-fill ${lowTime ? 'low' : ''}`}
              style={{ width: `${(remainingMs / DURATION_MS) * 100}%` }}
            />
          </div>
        </div>
        <div className="question-card">
          <span className="q-type-badge fluency">Fluency</span>
          <div className="q-text">{q.q}</div>
          <div className="options">
            {q.opts.map((text, i) => (
              <div
                key={i}
                className={`option${selected === i ? ' selected' : ''}`}
                onClick={() => select(i)}
              >
                <div className="option-letter">{LETTERS[i]}</div>
                <div className="option-text">{text}</div>
              </div>
            ))}
          </div>
          <div className="next-area">
            <button className="btn btn-primary" onClick={next} disabled={selected === null}>
              {cur === questions.length - 1 ? 'Submit →' : 'Next →'}
            </button>
          </div>
        </div>
      </>
    );
  }

  // done
  const score = questions.reduce((acc, q, i) => acc + (answers[i] === q.ans[0] ? 1 : 0), 0);
  const pct = Math.round((score / questions.length) * 100);
  const elapsedSec = Math.min(60, Math.round((Date.now() - startedAt) / 1000));
  const color = pct >= 90 ? 'var(--correct)' : pct >= 70 ? 'var(--gold)' : 'var(--wrong)';
  const wrong = questions
    .map((q, i) => ({ q, answer: answers[i] }))
    .filter(({ q, answer }) => answer !== q.ans[0]);

  return (
    <div className="results">
      <div className="results-circle" style={{ borderColor: color }}>
        <div className="results-pct" style={{ color }}>{pct}%</div>
        <div className="results-label">{score} / {questions.length}</div>
      </div>
      <h2>{pct === 100 ? 'Perfect!' : pct >= 90 ? 'Great fluency!' : pct >= 70 ? 'Good' : 'Keep practicing'}</h2>
      <p className="results-sub">{course.code} · Fluency · finished in {elapsedSec}s</p>
      <div className="results-actions">
        <button className="btn btn-primary" onClick={start}>Try Again →</button>
        <Link to="/" className="btn btn-secondary">All Courses</Link>
      </div>

      {wrong.length > 0 && (
        <div className="review-section">
          <h3>Review Missed ({wrong.length})</h3>
          {wrong.map(({ q, answer }, i) => (
            <div key={i} className="review-item">
              <div className="ri-q">{q.q}</div>
              <div className="ri-your">Your answer: {answer === null ? 'No answer (timed out)' : q.opts[answer]}</div>
              <div className="ri-correct">Correct: {q.opts[q.ans[0]]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
